/*
 * <delivery-countdown> shows how long is left to order for next business day
 * delivery. Orders placed before the cut-off on a business day are dispatched
 * that day and delivered the next business day.
 *
 * All dates are worked out on the UK calendar (Europe/London), whatever the
 * visitor's own time zone.
 */
(() => {
  if (customElements.get('delivery-countdown')) return;

  const CUTOFF_HOUR = 11;

  // England & Wales bank holidays (gov.uk). Add the next year's dates each autumn.
  const BANK_HOLIDAYS = new Set([
    '2026-01-01', '2026-04-03', '2026-04-06', '2026-05-04', '2026-05-25', '2026-08-31', '2026-12-25', '2026-12-28',
    '2027-01-01', '2027-03-26', '2027-03-29', '2027-05-03', '2027-05-31', '2027-08-30', '2027-12-27', '2027-12-28',
  ]);

  const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  const londonFormatter = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  });

  // Current UK date as a UTC-midnight Date (safe for day arithmetic) plus minutes past midnight.
  function londonNow() {
    const parts = Object.fromEntries(londonFormatter.formatToParts(new Date()).map((p) => [p.type, p.value]));
    return {
      date: new Date(Date.UTC(+parts.year, +parts.month - 1, +parts.day)),
      minutes: +parts.hour * 60 + +parts.minute,
    };
  }

  const addDays = (date, days) => new Date(date.getTime() + days * 86400000);
  const isoDate = (date) => date.toISOString().slice(0, 10);
  const daysBetween = (a, b) => Math.round((b - a) / 86400000);

  function isBusinessDay(date) {
    const day = date.getUTCDay();
    return day !== 0 && day !== 6 && !BANK_HOLIDAYS.has(isoDate(date));
  }

  function nextBusinessDay(date) {
    let next = addDays(date, 1);
    while (!isBusinessDay(next)) next = addDays(next, 1);
    return next;
  }

  function dayLabel(today, date) {
    const diff = daysBetween(today, date);
    if (diff === 0) return 'today';
    if (diff === 1) return 'tomorrow';
    if (diff < 7) return WEEKDAYS[date.getUTCDay()];
    return `${WEEKDAYS[date.getUTCDay()].slice(0, 3)} ${date.getUTCDate()} ${MONTHS[date.getUTCMonth()]}`;
  }

  function timeLeft(minutes) {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours === 0) return `${mins} min${mins === 1 ? '' : 's'}`;
    return `${hours}h ${mins}m`;
  }

  class DeliveryCountdown extends HTMLElement {
    connectedCallback() {
      this.render();
      this.timer = setInterval(() => this.render(), 30000);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    render() {
      const { date: today, minutes } = londonNow();
      const cutoff = CUTOFF_HOUR * 60;
      const dispatchToday = isBusinessDay(today) && minutes < cutoff;
      const dispatchDay = dispatchToday ? today : nextBusinessDay(today);
      const deliveryDay = nextBusinessDay(dispatchDay);
      const delivery = dayLabel(today, deliveryDay);

      const message = dispatchToday
        ? `Order within <strong>${timeLeft(cutoff - minutes)}</strong> for delivery <strong>${delivery}</strong>`
        : `Order by <strong>11am ${dayLabel(today, dispatchDay)}</strong> for delivery <strong>${delivery}</strong>`;

      this.innerHTML = `<span class="delivery-countdown__icon" aria-hidden="true">🦉</span><span>${message}</span>`;
    }
  }

  customElements.define('delivery-countdown', DeliveryCountdown);
})();
